import { Component, OnInit, Output, EventEmitter, OnDestroy, Input, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, Subscription } from 'rxjs';
import { SCFMaster } from '../../components/scf-v2/scf';
import { WorkerVerification } from 'src/app/services/worker-verification/worker-verification';
import { WorkerVerificationService } from 'src/app/services/worker-verification/worker-verification.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Document } from 'src/app/services/documents/documents';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { RSSWAResource } from 'src/app/components/rsswa/rsswa';
import { WorkerVerificationRefresh } from './store/worker-verification/worker-verification.action';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { RSF } from 'src/app/components/rsf/rsf';
import { Form } from 'src/app/services/forms/form';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { RolePrivilege } from 'src/app/components/catalogs/roles/services/role-privilege';

@Component({
  selector: 'worker-verification-table',
  templateUrl: './worker-verification.component.html',
  styleUrls: ['./worker-verification.component.scss']
})

export class WorkerVerificationComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() currentDocument?: SCFMaster | RSSWAResource | RSF | Document | null;
  @Input() disabled?: boolean;
  @Input() privilegeToSign!: PrivilegeEnum;
  /**
   * Human-readable name of the privilege required to sign (e.g. "RSF Worker"). Passing it opts a
   * host form into the enhanced UX: the "+" add button is always shown (during a signable status)
   * but disabled for users lacking the privilege, with an explanatory tooltip, and the non-prod
   * "Tester Approve" button is offered. Forms that do not pass it keep the original behavior.
   */
  @Input() signPrivilegeName?: string;
  /**
   * Locks signing regardless of status once the host form's workflow has moved past Worker
   * Verification (e.g. PPSTB after the Interlock Functional Test is signed). Hides add, remove and
   * tester actions. Kept separate from `disabled`, which some hosts bind to their edit mode.
   */
  @Input() signingLocked?: boolean;
  @Output() on_refresh_error = new EventEmitter<any>(false);

  signed: boolean = false;
  disableButtons?: boolean = true;

  dataSource = new MatTableDataSource<WorkerVerification>();
  displayed_columns = ['user', 'date', 'action'];
  workerVerifications?: WorkerVerification[];
  workerVerifications$!: Observable<WorkerVerification[]>;
  workerVerificationsSubs!: Subscription;

  warningMessage?: string;

  // US 6921: roles holding the sign privilege, shown so users know who can sign Worker Verification
  rolePrivileges: RolePrivilege[] = [];
  rolePrivilegesSubs!: Subscription;
  signRoleCodes?: string | null;

  private readonly enabledStatuses = new Set<FormStatusEnum>([
    FormStatusEnum.Active,
    FormStatusEnum.HoldPoint,
    FormStatusEnum.ActiveBypassInstalled,
    FormStatusEnum.ActiveOperationsVerified,
    // Bug 6926: PPSTB Interlock Functional Test is signed during Bypass Removed; keep signing open until then.
    FormStatusEnum.ActiveBypassRemoved,
    FormStatusEnum.ActiveMitigationsInPlace,
    FormStatusEnum.AmendmentDrafting,
    FormStatusEnum.AmendmentPending,
    FormStatusEnum.AmendmentApprovals,
    FormStatusEnum.PpsTesting,
    FormStatusEnum.PpsTestingComplete,
    FormStatusEnum.Active_AmendmentPendingRequesterConcurrence,
    FormStatusEnum.Active_AmendmentPendingSafetyControls,
    FormStatusEnum.Active_AmendmentReviewApprovals,
    FormStatusEnum.WorkCompleteFollowUps,
    FormStatusEnum.WorkCompleted
  ]);

  constructor(
    public service: WorkerVerificationService,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector
  ) {
    super(injector);
    this.store.dispatch(new WorkerVerificationRefresh());
  }

  /** Whether the host form opted into the enhanced (always-visible/disabled + tester) UX. */
  get enhancedSign(): boolean {
    return !!this.signPrivilegeName;
  }

  /** Tooltip for the "+" add button: informative when the user lacks the required privilege. */
  get addTooltip(): string {
    return this.hasPrivilege(this.privilegeToSign)
      ? 'Add Worker Verification Signature'
      : `Only users with the ${this.signPrivilegeName} Privilege can sign here`;
  }

  /**
   * Non-prod tester tool: whether the red "Tester Approve" button shows. Gated to non-production
   * hosts, System Administrators, the Tester Tools master switch, an opt-in host form
   * (signPrivilegeName), a signable status, and only when the SA cannot add the signature
   * themselves (they lack the privilege, so the normal "+" is disabled for them).
   */
  showTestApprove(): boolean {
    return (
      this.enhancedSign &&
      !this.environmentService.isProduction() &&
      this.isSA() &&
      this.testerToolsService.enabled &&
      !this.disableButtons &&
      !this.signed &&
      !this.hasPrivilege(this.privilegeToSign)
    );
  }

  /**
   * Non-prod tester tool: adds a Worker Verification signature on behalf of a user who holds the
   * required privilege (excluding those who already signed this form), so an SA without the
   * privilege can satisfy the section. The create endpoint already persists the client-supplied
   * userID, so this is front-end only. No confirmation popup; the tooltip is sufficient.
   */
  testApprove(): void {
    if (!this.showTestApprove()) return;
    const signedIds = this.workerVerifications?.map(w => w.userID) ?? [];
    const signer = this.getUsers().find(
      u => !!u.id && this.hasPrivilegeByUser(this.privilegeToSign, u.id!) && !signedIds.includes(u.id)
    );
    if (!signer?.id) {
      this.alert.warning('No eligible user with the required privilege was found to sign.');
      return;
    }
    const workerVerification: WorkerVerification = {
      form: this.currentDocument?.type,
      formID: this.currentDocument?.id,
      userID: signer.id,
      date: new Date(),
    };
    this.service.create(workerVerification).toPromise().then(() => {
      this.store.dispatch(new WorkerVerificationRefresh());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadWorkerVerifications();
    this.checkDisabled();
    // US 6921: the host may change the sign privilege, so recompute the allowed roles
    this.setSignRoleCodes();
  }

  override ngOnDestroy(): void {
    this.workerVerificationsSubs?.unsubscribe();
    this.rolePrivilegesSubs?.unsubscribe();
  }

  ngOnInit() {
    this.loadWorkerVerifications();
    this.loadRolePrivileges();
  }

  /**
   * Keeps the role-privilege catalog in sync (it is refreshed through SignalR) so the allowed-roles
   * list reflects permission changes without reloading the form.
   */
  loadRolePrivileges() {
    this.rolePrivilegesSubs = this.store.select(state => state.RolePrivileges.data).subscribe((data: RolePrivilege[]) => {
      this.rolePrivileges = data ?? [];
      this.setSignRoleCodes();
    });
  }

  /**
   * Builds the slash-separated, de-duplicated role codes (the format role-chips expects) for roles
   * holding `privilegeToSign` (Worker Verification for SCF/RSSWA/PPSTB, RSF Worker for RSF). Leaves it
   * empty for hosts that pass no privilege.
   */
  setSignRoleCodes() {
    if (!this.privilegeToSign) {
      this.signRoleCodes = null;
      return;
    }
    const codes = this.rolePrivileges
      .filter(rp => rp.privilege == this.privilegeToSign && rp.role?.code)
      .map(rp => rp.role.code as string);
    this.signRoleCodes = [...new Set(codes)].sort().join('/') || null;
  }

  /**
   * Disables signature actions outside signable statuses or when the host form has locked signing.
   */
  checkDisabled(): void {
    const status = this.currentDocument?.status;
    // Host-driven lock (e.g. PPSTB Interlock Functional Test signed) overrides the status check.
    this.disableButtons = !!this.signingLocked || !status || !this.enabledStatuses.has(status);
  }

  loadWorkerVerifications() {
    this.workerVerifications$ = this.store.select(state => state.WorkerVerification.data);
    this.workerVerificationsSubs = this.workerVerifications$.subscribe(data => {
      this.workerVerifications = data.filter(x => x.form == this.currentDocument?.type && x.formID == this.currentDocument?.id);
      this.signed = this.workerVerifications.some(w => w.userID == this.currentUser?.id);
      this.dataSource = new MatTableDataSource(this.workerVerifications);
    });
  }

  refresh(form: number, formID: number) {
    this.dataSource = new MatTableDataSource(this.workerVerifications?.filter(x => x.form == form && x.formID == formID));
  }

  clean() {
    this.dataSource.data = [];
  }

  addSignature() {
    let worker_verification: WorkerVerification = {
      form: this.currentDocument?.type,
      formID: this.currentDocument?.id,
      userID: this.currentUser?.id,
      date: new Date(),
    };

    const message = worker_verification.form == 1 ? "WorkerVerification_SignatureApprovement" : "WorkerVerification_SignatureApprovement_NonSCF";

    const confirmApprovement = this.dialog.open(YesNoDialogComponent, {
      width: "500px",
      data: {
        message: this.getMessage(message).description,
        icon: "warn",
      },
    });

    confirmApprovement.afterClosed().toPromise().then(async (data) => {
      if (data) {
        this.service.create(worker_verification).toPromise().then(() => {
          this.store.dispatch(new WorkerVerificationRefresh());
        });
      } else {
      }
    });
  }

  remove() {
    if (this.currentDocument?.id && this.currentDocument.type)
      this.service
        .deleteByFormUser(this.currentDocument.type, this.currentDocument.id, this.currentUser?.id ?? 0)
        .toPromise().then(() => {
          this.store.dispatch(new WorkerVerificationRefresh());
        });
  }
}
